import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, CaretDownIcon } from '@phosphor-icons/react';
import { Pagination } from '../../../components/ui/Pagination';
import { TableRowSkeleton } from '../../../components/ui/Skeleton';
import { DESIGNATION_COLORS } from '../designation';
import { formatIst } from '../formatIst';
import { useResearchChanges } from '../hooks/useResearchHistory';
import { BucketTag, ResearchHistorySheet } from './ResearchHistorySheet';
import type { ResearchChangesRow, ResearchFieldGroup } from '../../../services/dashboard.service';

const LIMIT = 20;
const SEARCH_DELAY_MS = 300;

const inputClass =
  'bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl px-4 py-2 text-xs text-slate-800 transition-smooth font-medium';

function WhatChanged({ row, labels }: { row: ResearchChangesRow; labels: Record<string, string> }) {
  const groups = Object.entries(row.summary?.groups ?? {}).filter(([, n]) => (n ?? 0) > 0);
  const reworded = row.summary?.reworded ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {groups.length === 0 && <span className="text-slate-400">No value changes</span>}
      {groups.map(([g, n]) => (
        <span key={g} className="px-1.5 py-0.5 rounded-md bg-accent/10 text-accent text-[10px] font-bold">
          {labels[g] ?? g} {n}
        </span>
      ))}
      {reworded > 0 && <span className="text-[10px] text-slate-400">+{reworded} reworded</span>}
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange }: { label: string, options: string[], selected: string[], onChange: (s: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button 
        onClick={() => setOpen(!open)}
        className="border border-slate-200/60 bg-slate-50 hover:bg-white px-3 py-2 rounded-xl text-xs font-medium text-slate-700 flex items-center justify-between min-w-[140px] transition-colors focus:ring-4 focus:ring-accent/10 focus:border-accent"
      >
        <span className="truncate">{selected.length ? `${label} (${selected.length})` : label}</span>
        <CaretDownIcon className={`ml-2 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto p-1.5 flex flex-col gap-0.5">
          {options.length === 0 && <div className="p-2 text-xs text-slate-400">Loading...</div>}
          {options.map(opt => (
            <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded-lg cursor-pointer text-xs text-slate-700 transition-colors">
              <input 
                type="checkbox" 
                checked={selected.includes(opt)} 
                onChange={e => {
                  if (e.target.checked) onChange([...selected, opt]);
                  else onChange(selected.filter(x => x !== opt));
                }}
                className="accent-accent w-3.5 h-3.5 rounded border-slate-300 text-accent focus:ring-accent"
              />
              <span className="truncate" title={opt}>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const BUSINESS_SECTORS = [
  "MANUFACTURING/HEAVY MACHINERY/METALLURGY", "FMCG, CONSUMER GOODS AND RETAIL",
  "FINANCE, BANKING AND VENTURE CAPITAL", "ENTERTAINMENT, MEDIA & SPORTS",
  "TECHNOLOGY, HEALTHCARE", "STRATEGY CONSULTING & CORPORATE ADVISORY",
  "SOFTWARE, SAAS & CONSUMER TECH PLATFORM", "PUBLIC POLICY, GOVERNANCE & SOCIAL IMPACT",
  "INFRASTRUCTURE, CIVIL ENGG", "SEMICONDUCTORS, ELECTRONICS & ELECTRICAL EQUIPMENT",
  "AUTOMOTIVE & MOBILITY", "ENERGY, OIL & GAS & RENEWABLE ENERGY",
  "AEROSPACE, DEFENCE & SPACE", "PHARMA, BIOTECH & MEDICAL DEVICES",
  "CHEMICALS & SPECIALTY MATERIALS", "CLEAN TECH, CLIMATE & ENVIRONMENT",
  "EDUCATION & EDTECH", "AGRICULTURE, AGRITECH & FOOD PROCESSING",
  "ARCHITECTURE, DESIGN & CREATIVE SERVICES", "NON-PROFIT, PHILANTHROPY & DEVELOPMENT ORGANIZATIONS",
  "OTHER"
];

/**
 * The Alumni Directory's "Research changes" view: one row per profile whose AI
 * research has been rerun, with what its latest rerun changed. A row opens the
 * profile's full history.
 */
export function ResearchChangesTable() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [group, setGroup] = useState<ResearchFieldGroup | ''>('');
  const [changedOnly, setChangedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<ResearchChangesRow | null>(null);

  const [designations, setDesignations] = useState<string[]>(searchParams.getAll('designations') || []);
  const [sectors, setSectors] = useState<string[]>(searchParams.getAll('sectors') || []);
  const [locations, setLocations] = useState<string[]>(searchParams.getAll('locations') || []);
  const [targetAsks, setTargetAsks] = useState<string[]>(searchParams.getAll('target_asks') || []);
  
  // Need to fetch target ask options
  const [targetAskOptions, setTargetAskOptions] = useState<string[]>([]);
  useEffect(() => {
    import('../../../services/dashboard.service').then(({ dashboardService }) => {
      dashboardService.getBucketedConstellation({ bucket_by: 'target_ask' }).then(res => {
        setTargetAskOptions((res.data.bucket_ranges || []).map(r => r.label));
      });
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [search]);

  const { data, loading, error } = useResearchChanges({
    q: q || undefined,
    sectors: sectors.length ? sectors : undefined,
    locations: locations.length ? locations : undefined,
    target_asks: targetAsks.length ? targetAsks : undefined,
    designations: designations.length ? designations : undefined,
    group: group || undefined,
    changed_only: changedOnly || undefined,
    page,
    limit: LIMIT,
  });
  const rows = data?.rows ?? [];
  const labels = Object.fromEntries((data?.groups ?? []).map((g) => [g.key, g.label]));

  return (
    <>
      <div className="flex flex-col gap-3 p-5 border-b border-slate-100">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative w-full sm:max-w-xs">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <MagnifyingGlassIcon size={15} weight="bold" />
            </span>
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className={`w-full pl-10 ${inputClass} py-2.5`}
            />
          </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MultiSelect 
              label="Designations" 
              options={Object.keys(DESIGNATION_COLORS)} 
              selected={designations} 
              onChange={v => { setDesignations(v); setPage(1); }} 
            />
            <MultiSelect 
              label="Sectors" 
              options={BUSINESS_SECTORS} 
              selected={sectors} 
              onChange={v => { setSectors(v); setPage(1); }} 
            />
            <MultiSelect 
              label="Target Ask Range" 
              options={targetAskOptions} 
              selected={targetAsks} 
              onChange={v => { setTargetAsks(v); setPage(1); }} 
            />
            {/* simple input for locations since they are free text, or we can use an input text and add it as tags. The user requested independent multi-select dropdowns for Sector, Location, Target Ask Range, and Designation. Since Locations are free text, we can just use a text input, but user said "multi-select dropdowns". A multi-select for free text doesn't make sense unless we have options. We will provide a simple free text input for location since options are unbounded. */}
            <input
              type="text"
              placeholder="Locations (comma separated)"
              value={locations.join(', ')}
              onChange={(e) => { setLocations(e.target.value.split(',').map(s => s.trim()).filter(Boolean)); setPage(1); }}
              className={`w-full sm:w-48 ${inputClass} py-2`}
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={group}
            onChange={(e) => { setGroup(e.target.value as ResearchFieldGroup | ''); setPage(1); }}
            className={`w-full sm:w-52 ${inputClass}`}
          >
            <option value="">Changed in any group</option>
            {(data?.groups ?? []).map((g) => (
              <option key={g.key} value={g.key}>Changed in {g.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={changedOnly}
              onChange={(e) => { setChangedOnly(e.target.checked); setPage(1); }}
              className="accent-current"
            />
            Only with value changes
          </label>
          <div className="sm:ml-auto text-[10px] text-slate-400 font-bold uppercase tracking-wider whitespace-nowrap">
            Re-researched: <span className="text-slate-800 font-extrabold">{data?.total ?? 0}</span>
          </div>
        </div>
        <p className="text-[11px] text-slate-400">
          Each row is a profile's latest AI research rerun compared with the run before it. Filters apply to that rerun.
        </p>
      </div>

      <div className="overflow-auto shrink-0" style={{ height: '680px' }}>
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 bg-white z-20 shadow-sm">
            <tr className="border-b border-slate-100 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
              <th className="pb-3 pt-3 pl-5 w-[22%]">Alumni Name</th>
              <th className="pb-3 pt-3 w-[14%]">Designation Bucket</th>
              <th className="pb-3 pt-3 w-[20%]">Last Rerun</th>
              <th className="pb-3 pt-3 w-[36%]">What Changed</th>
              <th className="pb-3 pt-3 pr-5 text-right w-[8%]">Reruns</th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {loading && !rows.length ? (
              <TableRowSkeleton cols={5} rows={6} />
            ) : error ? (
              <tr><td colSpan={5} className="py-10 text-center text-rose-600">{error}</td></tr>
            ) : !rows.length ? (
              <tr>
                <td colSpan={5} className="py-10 text-center text-slate-400">
                  {q || sectors.length || locations.length || targetAsks.length || designations.length || group || changedOnly
                    ? 'No reruns match these filters.'
                    : 'No profile has been re-researched yet. Changes appear here after a profile’s AI research is run again.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.profile_id}
                  onClick={() => setOpen(row)}
                  className={`border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer transition-colors ${loading ? 'opacity-60' : ''}`}
                >
                  <td className="py-4 pl-5 font-bold text-slate-800">{row.name || 'Alumni'}</td>
                  <td className="py-4 text-slate-600"><BucketTag category={row.designation_category} /></td>
                  <td className="py-4 text-slate-500">
                    <div className="font-semibold text-slate-700">{formatIst(row.researched_at)}</div>
                    <div className="text-[10px]">
                      {row.trigger === 'scheduled' ? 'Scheduled' : 'Manual'}
                      {row.researched_by ? ` · ${row.researched_by}` : ''}
                    </div>
                  </td>
                  <td className="py-4"><WhatChanged row={row} labels={labels} /></td>
                  <td className="py-4 pr-5 text-right font-bold text-slate-700">{row.reruns}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > 0 && (
        <div className="px-5 pb-5">
          <Pagination
            page={page}
            totalPages={data.pages}
            totalItems={data.total}
            itemsPerPage={LIMIT}
            itemLabel="profile"
            onPageChange={setPage}
          />
        </div>
      )}

      <ResearchHistorySheet
        alumniId={open?.profile_id ?? null}
        name={open?.name}
        onClose={() => setOpen(null)}
      />
    </>
  );
}
